import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ExperienceStyle from "../../assets/stylesheets/experience/Experience.module.css";
import AppStyle from "../../assets/stylesheets/App.module.css";
import BannerSlider from "../../components/BannerSlider";
import ExperienceFilter from "../../components/experience/ExperienceFilter";
import ExperienceList from "../../components/experience/ExperienceList";
import { listPublicCards, getCoverImageByExperienceId } from "../../api/farm-service/experienceService";
import Logger from "../../utils/logger";

const log = Logger.create("FarmExperiencePage");

// Mock data - will be replaced by API data when backend is ready
const MOCK_EXPERIENCES = {
  featured: [
    { id: 1, title: "Apple Picking & Cider Tasting", farmName: "Sunny Ridge Orchard", duration: "2.5 hours", groupSize: 10, rating: 4.9, reviewCount: 156, price: 45, badge: "Popular", coverImage: { viewUrl: "https://images.unsplash.com/photo-1570913149827-d2ac84ab3f9a?w=600&h=400&fit=crop" }, category: "harvest" },
    { id: 2, title: "Meet & Feed Farm Animals", farmName: "Green Valley Farm", duration: "1.5 hours", groupSize: 8, rating: 4.8, reviewCount: 203, price: 35, badge: "Family Favorite", coverImage: { viewUrl: "https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=600&h=400&fit=crop" }, category: "animals" },
    { id: 3, title: "Cheese Making Workshop", farmName: "Heritage Dairy Farm", duration: "3 hours", groupSize: 12, rating: 5.0, reviewCount: 89, price: 75, badge: "Top Rated", coverImage: { viewUrl: "https://images.unsplash.com/photo-1486297678162-eb2a19b0a32d?w=600&h=400&fit=crop" }, category: "workshops" },
    { id: 4, title: "Sunset Farm Tour & Dinner", farmName: "Harvest Moon Farm", duration: "4 hours", groupSize: 20, rating: 4.9, reviewCount: 142, price: 95, coverImage: { viewUrl: "https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=600&h=400&fit=crop" }, category: "food" }
  ],
  seasonal: [
    { id: 5, title: "Pumpkin Patch Adventure", farmName: "Autumn Fields Farm", duration: "2 hours", groupSize: 15, rating: 4.7, reviewCount: 234, price: 30, badge: "Fall Season", coverImage: { viewUrl: "https://images.unsplash.com/photo-1509557965875-b88c97052f0e?w=600&h=400&fit=crop" }, category: "seasonal" },
    { id: 6, title: "Christmas Tree Cutting", farmName: "Evergreen Tree Farm", duration: "2 hours", groupSize: 6, rating: 4.8, reviewCount: 178, price: 40, badge: "Holiday Special", coverImage: { viewUrl: "https://images.unsplash.com/photo-1512389142860-9c449e58a543?w=600&h=400&fit=crop" }, category: "seasonal" },
    { id: 7, title: "Spring Lamb Feeding", farmName: "Meadow Brook Farm", duration: "1 hour", groupSize: 10, rating: 4.9, reviewCount: 167, price: 25, coverImage: { viewUrl: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop" }, category: "seasonal" }
  ],
  kids: [
    { id: 8, title: "Junior Farmer Program", farmName: "Little Sprouts Farm", duration: "3 hours", groupSize: 12, rating: 5.0, reviewCount: 95, price: 50, badge: "Educational", coverImage: { viewUrl: "https://images.unsplash.com/photo-1560493676-04071c5f467b?w=600&h=400&fit=crop" }, category: "kids" },
    { id: 9, title: "Pony Rides & Petting Zoo", farmName: "Happy Trails Farm", duration: "1.5 hours", groupSize: 8, rating: 4.8, reviewCount: 312, price: 30, coverImage: { viewUrl: "https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=600&h=400&fit=crop" }, category: "kids" },
    { id: 10, title: "Egg Hunting Adventure", farmName: "Cluckin' Good Farm", duration: "1 hour", groupSize: 15, rating: 4.7, reviewCount: 128, price: 20, coverImage: { viewUrl: "https://images.unsplash.com/photo-1548550023-2bdb3c5beed7?w=600&h=400&fit=crop" }, category: "kids" }
  ],
  workshops: [
    { id: 11, title: "Beekeeping Basics", farmName: "Honey Haven Farm", duration: "2 hours", groupSize: 8, rating: 4.9, reviewCount: 76, price: 60, coverImage: { viewUrl: "https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=600&h=400&fit=crop" }, category: "workshops" },
    { id: 12, title: "Farm-to-Table Cooking Class", farmName: "Garden Grove Farm", duration: "3.5 hours", groupSize: 10, rating: 5.0, reviewCount: 134, price: 85, badge: "Chef's Choice", coverImage: { viewUrl: "https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=600&h=400&fit=crop" }, category: "workshops" }
  ]
};

const EXPERIENCE_BANNERS = [
  { image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&h=400&fit=crop", title: (<>Unforgettable Farm<br />Experiences Await</>), description: "Connect with nature, learn new skills, and create lasting memories at local farms" },
  { image: "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1200&h=400&fit=crop", title: (<>Book Your Next<br />Farm Adventure</>), description: "From harvest activities to workshops, discover unique experiences for all ages" }
];

// Set to true to use mock data, false to use API
const USE_MOCK_DATA = false;

/**
 * Sample experiences injected into the list to fill empty sections.
 * These use "sample-X" IDs so ExperienceDetailPage can serve mock detail pages.
 */
const SAMPLE_EXPERIENCES = [
  {
    id: "sample-1",
    title: "Honey & Beekeeping Discovery",
    farmName: "Clover Hill Apiary",
    duration: "2 hours",
    groupSize: 8,
    price: 65,
    badge: "Staff Pick",
    banner: "Staff Pick",
    season: null,
    coverImageUrl: "https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=600&h=400&fit=crop",
    createdAt: "2026-04-20T10:00:00Z",
  },
  {
    id: "sample-2",
    title: "Lavender Harvest & Wreath Making",
    farmName: "Purple Meadow Farm",
    duration: "3 hours",
    groupSize: 12,
    price: 55,
    badge: "Summer",
    banner: null,
    season: "summer",
    coverImageUrl: "https://images.unsplash.com/photo-1499002238440-d264edd596ec?w=600&h=400&fit=crop",
    createdAt: "2026-04-18T10:00:00Z",
  },
];

/** BFF 응답에서 experiences 배열 추출 */
function extractExperiences(res) {
  const d1 = res?.data ?? res;
  const d2 = d1?.data ?? d1;
  return Array.isArray(d2) ? d2 : Array.isArray(d2?.experiences) ? d2.experiences : [];
}

/**
 * Groups a flat experience list into Workshop / Seasonal / New / Staff Pick.
 *
 * - Workshop  : no `season` value (type-based or catch-all non-seasonal)
 * - Seasonal  : has `season` field (spring | summer | fall | winter)
 * - New       : most recently created (up to 8, sorted by createdAt desc)
 * - Staff Pick: has a non-empty `banner` field
 */
function groupByCategory(list) {
  const result = { workshop: [], seasonal: [], newArrivals: [], staffPick: [] };
  for (const exp of list) {
    const hasSeason = Boolean(exp.season);
    const hasBanner = Boolean(exp.banner);

    if (hasBanner) result.staffPick.push(exp);
    if (hasSeason) result.seasonal.push(exp);
    else result.workshop.push(exp);
  }
  // New: top-8 by createdAt descending across all items
  result.newArrivals = [...list]
    .sort((a, b) => new Date(b.createdAt ?? 0) - new Date(a.createdAt ?? 0))
    .slice(0, 8);
  return result;
}

export default function FarmExperiencePage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isScrolled, setIsScrolled] = useState(false);

  const [experiences, setExperiences] = useState(
    USE_MOCK_DATA ? MOCK_EXPERIENCES : { workshop: [], seasonal: [], newArrivals: [], staffPick: [] }
  );
  const [loading, setLoading] = useState(!USE_MOCK_DATA);
  const [error, setError] = useState(null);

  const fetchExperiences = () => {
    if (USE_MOCK_DATA) return;
    setLoading(true);
    setError(null);
    listPublicCards()
      .then(async (res) => {
        const list = extractExperiences(res);

        // Fetch cover image URL for each experience in parallel
        const enriched = await Promise.allSettled(
          list.map((exp) =>
            exp.coverImageId
              ? getCoverImageByExperienceId(exp.id)
                  .then((r) => {
                    // Response: { data: { success, data: { id, viewUrl } } }
                    const viewUrl = r?.data?.data?.viewUrl ?? r?.data?.viewUrl ?? null;
                    return { ...exp, coverImageUrl: viewUrl };
                  })
                  .catch(() => exp)
              : Promise.resolve(exp)
          )
        ).then((results) => results.map((r) => (r.status === "fulfilled" ? r.value : r.reason)));

        setExperiences(groupByCategory([...enriched, ...SAMPLE_EXPERIENCES]));
      })
      .catch((err) => {
        log.error("Failed to load experiences:", err);
        setError("Failed to load experiences.");
      })
      .finally(() => setLoading(false));
  };

  // Fetch experiences from API
  useEffect(() => {
    fetchExperiences();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 1);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (error) {
      log.error("Failed to load experiences:", error);
    }
  }, [error]);

  const handleExperienceClick = (experience) => {
    log.info("Experience clicked:", experience.id);
    navigate(`/experience/${experience.id}`);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    // TODO: Filter experiences or fetch by category
    log.info("Category changed:", category);
  };

  if (loading && !USE_MOCK_DATA) {
    return (
      <div className={ExperienceStyle["experience-page"]}>
        <main className={ExperienceStyle["experience-main-content"]}>
          <div style={{ padding: "var(--gap-lg)", textAlign: "center" }}>
            <p>Loading experiences...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={ExperienceStyle["experience-page"]}>
      <main className={ExperienceStyle["experience-main-content"]}>
        
        {/* Development Notice — remove once API is stable */}
        {USE_MOCK_DATA && (
          <section className={`${AppStyle["section"]}`}>
            <div className={AppStyle["container"]}>
              <div className={`${ExperienceStyle.notice} ${ExperienceStyle['notice--blue']}`} role="note">
                <span aria-hidden="true" className={ExperienceStyle['notice-emoji']}>ℹ️</span>
                <p className={ExperienceStyle['notice-text']}>
                  Farm Experiences is under active development. We're building something exciting. The page you're viewing is a mockup and is not yet functional.
                </p>
              </div>
            </div>
          </section>
        )}

        <section className={`${AppStyle["section"]} ${AppStyle["banner-section"]}`}>
          <BannerSlider slides={EXPERIENCE_BANNERS} />
        </section>

        {/* <ExperienceFilter selectedCategory={selectedCategory} onCategoryChange={handleCategoryChange} /> */}

        {/* Show error message if API fails */}
        {error && !USE_MOCK_DATA && (
          <section className={`${AppStyle["section"]}`}>
            <div className={AppStyle["container"]}>
              <div style={{ padding: "var(--gap-md)", textAlign: "center", color: "var(--color-text-accent)" }}>
                <p>Failed to load experiences. Please try again later.</p>
        <button onClick={fetchExperiences} style={{ marginTop: "var(--gap-sm)" }}>Retry</button>
              </div>
            </div>
          </section>
        )}

        <section className={`${AppStyle["section"]} ${AppStyle["card-section"]}`}>
          <div className={AppStyle["container"]}>
            <ExperienceList
              title="Workshops"
              subtitle="Learn new skills from passionate artisans and farmers"
              experiences={experiences.workshop}
              onExperienceClick={handleExperienceClick}
              loading={loading && !USE_MOCK_DATA}
            />
          </div>
        </section>

        <section className={`${AppStyle["section"]} ${AppStyle["card-section"]}`}>
          <div className={AppStyle["container"]}>
            <ExperienceList
              title="Seasonal Activities"
              subtitle="Experience the best of each season on the farm"
              experiences={experiences.seasonal}
              onExperienceClick={handleExperienceClick}
              loading={loading && !USE_MOCK_DATA}
            />
          </div>
        </section>

        <section className={`${AppStyle["section"]} ${AppStyle["banner-section"]}`}>
          <div className={ExperienceStyle["info-banner"]}>
            <div className={ExperienceStyle["info-content"]}>
              <h2>Why Book Farm Experiences?</h2>
              <div className={ExperienceStyle["info-grid"]}>
                <div className={ExperienceStyle["info-item"]}>
                  <span className={ExperienceStyle["info-icon"]}>🌱</span>
                  <h3>Connect with Nature</h3>
                  <p>Experience authentic farm life and learn sustainable practices</p>
                </div>
                <div className={ExperienceStyle["info-item"]}>
                  <span className={ExperienceStyle["info-icon"]}>👨‍🌾</span>
                  <h3>Learn from Experts</h3>
                  <p>Get hands-on guidance from experienced farmers and artisans</p>
                </div>
                <div className={ExperienceStyle["info-item"]}>
                  <span className={ExperienceStyle["info-icon"]}>👨‍👩‍👧‍👦</span>
                  <h3>Family-Friendly</h3>
                  <p>Create lasting memories with activities for all ages</p>
                </div>
                <div className={ExperienceStyle["info-item"]}>
                  <span className={ExperienceStyle["info-icon"]}>🎯</span>
                  <h3>Small Groups</h3>
                  <p>Intimate experiences with personalized attention</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={`${AppStyle["section"]} ${AppStyle["card-section"]}`}>
          <div className={AppStyle["container"]}>
            <ExperienceList
              title="New Experiences"
              subtitle="Freshly added — be among the first to discover them"
              experiences={experiences.newArrivals}
              onExperienceClick={handleExperienceClick}
              loading={loading && !USE_MOCK_DATA}
            />
          </div>
        </section>

        <section className={`${AppStyle["section"]} ${AppStyle["card-section"]}`}>
          <div className={AppStyle["container"]}>
            <ExperienceList
              title="Staff Picks"
              subtitle="Our team's favourite experiences, hand-selected for you"
              experiences={experiences.staffPick}
              onExperienceClick={handleExperienceClick}
              loading={loading && !USE_MOCK_DATA}
            />
          </div>
        </section>

        <section className={`${AppStyle["section"]} ${ExperienceStyle["cta-section"]}`}>
          <div className={ExperienceStyle["cta-banner"]}>
            <h2>Have a Farm Experience to Share?</h2>
            <p>Join FarmCiti and showcase your unique farm activities to thousands of visitors</p>
            <a href="mailto:farmertoronto@gmail.com" className={ExperienceStyle["cta-button"]}>Partner With Us</a>
          </div>
        </section>

      </main>
    </div>
  );
}
